import { useAppDispatch, useAppSelector } from "./../../../../hooks";
import { useCallback, useEffect, useMemo } from "react";
import { NotExist } from "./../../../layouts/NotData";
import {
  Button,
  Card,
  Flex,
  Loading,
  SecureCopy,
  Tag,
  Tooltip,
  Typography,
} from "djuno-design";
import {
  AiNotebooksAttachedDataUrl,
  AiNotebooksUrl,
} from "../../../../utils/urls";
import { useNavigate } from "react-router-dom";
import {
  getAiNotebookAsync,
  selectAiNotebook,
  selectAiNotebookLoading,
} from "../../../../store/ai/notebooks/notebookSlice";
import CustomLink from "../../../general/CustomLink";
import { AiNotebookStatusTag } from "../../../../pages/ai-notebooks/AiNotebooksPage";
import { formatDuration, formatTimestamp } from "../../../../utils/date";
import CheckIcon from "./../../../../assets/icons/check.svg?react";
import ArrowIcon from "./../../../../assets/icons/arrow-top-right-on-square.svg?react";
import InfoIcon from "./../../../../assets/icons/question-mark-circle.svg?react";
import {
  getAiResourceFlavorsAsync,
  handleToggleAiNotebookLabelModal,
  selectAiResourceFlavors,
  updateAiNotebookAsync,
} from "../../../../store/ai/notebooks/notebooksSlice";
import { humanizeSize } from "../../../../utils/file";
import AiNotebookLabelModal from "./AiNotebookLabelModal";
import { deleteItemFromObject } from "../../../../utils";

const AiGeneralInformationTab = () => {
  const notebook = useAppSelector(selectAiNotebook);
  const notebookLoading = useAppSelector(selectAiNotebookLoading);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const flavors = useAppSelector(selectAiResourceFlavors);

  useEffect(() => {
    if (notebook)
      dispatch(getAiResourceFlavorsAsync({ region: notebook.spec.region }));
  }, [dispatch, notebook]);

  const flavorData = useMemo(() => {
    return flavors.find((f) => f.id === notebook?.spec.resources.flavor);
  }, [notebook, flavors]);

  const handleDeleteLabel = useCallback(
    (labelKey: string) => {
      if (!notebook) return;
      const labels = deleteItemFromObject(notebook.spec.labels, labelKey);

      dispatch(
        updateAiNotebookAsync({
          id: notebook.id,
          data: { labels },
        })
      ).then((action) => {
        if (action.type === "db-services/update/fulfilled") {
          dispatch(
            getAiNotebookAsync({
              id: notebook.id,
              withoutLoading: true,
            })
          );
        }
      });
    },
    [dispatch, notebook]
  );

  return (
    <>
      {notebook && !notebookLoading && (
        <Flex direction="col" className="gap-5">
          {/* <Alert
            description="To use AI Tools, please ensure that you are using a configured AI user or a token.
            Manage my AI users and tokens"
            uiType="info"
            closable
            showIcon
          /> */}
          <Card title="General information">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Notebook name
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {notebook.spec.name}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Live-code editor
                  </Typography.Title>

                  <Button
                    disabled={notebook.status.state !== "RUNNING"}
                    uiSize="small"
                    onClick={() =>
                      notebook.status.url &&
                      window.open(notebook.status.url, "_blank")
                    }
                  >
                    {notebook.spec.env.editorId}
                    <ArrowIcon className="w-4 aspect-square flex-shrink-0" />
                  </Button>
                </div>

                {notebook.status.sshUrl && (
                  <div className="col-span-12 lg:col-span-6 w-full">
                    <Typography.Title level={6} className="!text-sm">
                      SSH access
                    </Typography.Title>
                    <SecureCopy
                      type="copy"
                      text={notebook.status.sshUrl}
                      uiSize="small"
                      className="!max-w-md"
                    />
                  </div>
                )}

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Attached containers
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {notebook.spec.volumes.length} container(s)
                  </Typography.Text>
                  <CustomLink
                    to={AiNotebooksAttachedDataUrl(notebook.id)}
                    className="!text-sm flex items-center gap-1"
                  >
                    View attached data
                    <ArrowIcon className="w-4 flex-shrink-0" />
                  </CustomLink>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Tags
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {notebook.spec.labels !== null &&
                      Object.keys(notebook.spec.labels).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {Object.keys(notebook.spec.labels).map((key) => (
                            <Tag
                              key={key}
                              onClose={() => handleDeleteLabel(key)}
                              closable
                            >
                              {
                                notebook.spec.labels![
                                  key as keyof typeof notebook.spec.labels
                                ]
                              }
                            </Tag>
                          ))}
                        </div>
                      )}
                  </Typography.Text>
                  <Typography.Link
                    onClick={() =>
                      dispatch(
                        handleToggleAiNotebookLabelModal({
                          isOpen: true,
                          notebook,
                        })
                      )
                    }
                    className="!text-sm flex items-center gap-1 mt-1 cursor-pointer"
                  >
                    Add a label
                    <ArrowIcon className="w-4 flex-shrink-0" />
                  </Typography.Link>
                  <AiNotebookLabelModal />
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Environment
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {notebook.spec.env.frameworkId} -{" "}
                    {notebook.spec.env.frameworkVersion}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Region
                  </Typography.Title>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {notebook.spec.region}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Lifecycle">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Status
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    <AiNotebookStatusTag state={notebook.status.state} />
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Runtime
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {formatDuration(notebook.status.duration)}
                  </Typography.Text>
                  {/* <CustomLink
                    to={AiNotebooksAttachedDataUrl(notebook.id)}
                    className="!text-sm flex items-center gap-1"
                  >
                    View billing
                    <ArrowIcon className="w-4 flex-shrink-0" />
                  </CustomLink> */}
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Creation date
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {
                      formatTimestamp(notebook.createdAt, "D MMM YYYY HH:mm", {
                        isUTC: false,
                      }).datetime
                    }
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm !mb-5">
                    Timeline
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    <CheckIcon className="w-4 flex-shrink-0 text-primary-300" />
                    Creation date:{"  "}
                    {
                      formatTimestamp(
                        notebook.createdAt,
                        "D MMM YYYY HH:mm:ss",
                        {
                          isUTC: false,
                        }
                      ).datetime
                    }
                  </Typography.Text>

                  {notebook.status.lastStartedAt && (
                    <Typography.Text className="!text-sm flex items-center gap-1">
                      <CheckIcon className="w-4 flex-shrink-0 text-primary-300" />
                      Restart date:{"  "}
                      {
                        formatTimestamp(
                          notebook.status.lastStartedAt,
                          "D MMM YYYY HH:mm:ss",
                          {
                            isUTC: false,
                          }
                        ).datetime
                      }
                    </Typography.Text>
                  )}

                  {/* {notebook.status.history.map((history, index) => (
                    <Typography.Text
                      key={index}
                      className="!text-xs flex items-center gap-1 mb-1"
                      uiType={
                        notebook.status.history.length === index + 1
                          ? undefined
                          : "secondary"
                      }
                    >
                      <CheckIcon
                        className={cn("w-4 flex-shrink-0 ", {
                          "text-primary-300":
                            notebook.status.history.length === index + 1,
                        })}
                      />
                      <AiNotebookStatusTag state={history.state} />:{"  "}
                      {
                        formatTimestamp(history.date, "D MMM YYYY HH:mm:ss", {
                          isUTC: false,
                        }).datetime
                      }
                    </Typography.Text>
                  ))} */}
                </div>
              </div>
            </div>
          </Card>

          <Card title="Resources">
            <div className="p-2">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Resources
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {/* TODO */}1 ×{" "}
                    {notebook.spec.resources.flavor.toUpperCase()}
                  </Typography.Text>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    {/* TODO */}1 × {flavorData?.description}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Other resources
                  </Typography.Title>

                  <Typography.Text className="!text-sm flex items-center gap-1">
                    RAM:{" "}
                    {humanizeSize(notebook.spec.resources.memory).join("B")}
                  </Typography.Text>
                  <Typography.Text className="!text-sm flex items-center gap-1">
                    Public network:{" "}
                    {humanizeSize(notebook.spec.resources.publicNetwork, {
                      binaryBaseValue: 1000,
                    }).join("B/s")}
                  </Typography.Text>
                </div>

                <div className="col-span-12 lg:col-span-6 w-full">
                  <Typography.Title level={6} className="!text-sm">
                    Storage
                  </Typography.Title>

                  <Typography.Text className="flex items-center gap-1 !text-sm whitespace-nowrap mt-5 mb-2 ">
                    Temporary local storage size:{" "}
                    {humanizeSize(
                      notebook.spec.resources.ephemeralStorage
                    ).join("B")}{" "}
                    SSD
                    <Tooltip content="High-performance local storage, but not backed up">
                      {" "}
                      <InfoIcon className="dd-w-4 dd-text-slate-500 dark:dd-text-slate-300 dark:hover:dd-text-slate-100" />
                    </Tooltip>
                  </Typography.Text>

                  {notebook.status.workspace && (
                    <Typography.Text className="flex items-center gap-1 !text-sm whitespace-nowrap mt-5 mb-2 ">
                      Workspace:{" "}
                      {humanizeSize(notebook.status.workspace.storageFree).join(
                        "B"
                      )}{" "}
                      SSD included{" "}
                      <Tooltip content="Remote storage backed up. After 30 consecutive days of storage or/and exceeding this quota of 10 GiB, the prices applied will be those for Public Cloud Object Storage.">
                        {" "}
                        <InfoIcon className="dd-w-4 dd-text-slate-500 dark:dd-text-slate-300 dark:hover:dd-text-slate-100" />
                      </Tooltip>
                    </Typography.Text>
                  )}
                  {notebook.status.workspace && (
                    <Typography.Text className="!text-xs">
                      {`${
                        humanizeSize(notebook.status.workspace.storageUsed)
                          .number
                      }/${humanizeSize(
                        notebook.status.workspace.storageFree
                      ).join("B")}`}
                    </Typography.Text>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Flex>
      )}

      {/* empty service */}
      {notebook === null && !notebookLoading && (
        <div className="h-full flex items-center justify-center px-4">
          <div className="flex flex-col items-center">
            <div className="text-center space-y-1">
              <NotExist
                url={() => navigate(AiNotebooksUrl)}
                text="The notebook does not exist"
              />
            </div>
          </div>
        </div>
      )}

      {notebookLoading && (
        <Flex items="center" justify="center" className="w-full !min-h-[300px]">
          <Loading borderSize={2} />
        </Flex>
      )}
    </>
  );
};

export default AiGeneralInformationTab;
